import { put, select } from 'typed-redux-saga';

import { createLogger } from '../../../shared/helpers/loggerFacade';
import { GRID_VIEW } from '../constants';
import { createChatAction, toggleChatVisibilityAction } from '../features/chat';
import { cancelCallAction } from '../features/conference/slice';
import { toggleMuteAction } from '../features/device/slice';
import { nextGridPageAction, prevGridPageAction } from '../features/goloom-video-grid';
import { setUserPreferredGridViewAction } from '../features/goloom-video-grid/slice';
import { setReactionAction } from '../features/reaction';
import { participantsScrollController } from '../features/sidebar/participants-scroll-controller';
import { setSidebarContent } from '../features/sidebar/slice';
import { getChatId, getSidebarContent, isChatAllowedInConference } from '../selectors';
import { SidebarContent } from '../types';
import { screenRecordingAction } from './slice';
import { IncomingEvents, IncomingPostMessage } from './types';

const logger = createLogger('handleIframeMessage');

export function* handleMeetingRoomMessage(message: IncomingPostMessage) {
    switch (message.event) {
        case IncomingEvents.SET_MUTE: {
            yield* put(toggleMuteAction(message.payload));
            break;
        }

        case IncomingEvents.CANCEL_CALL: {
            yield* put(cancelCallAction());
            break;
        }

        case IncomingEvents.START_SCREEN_RECORDING_RESULT:
        case IncomingEvents.ADD_SCREEN_RECORDING_CHUNK_RESULT:
        case IncomingEvents.END_SCREEN_RECORDING_RESULT: {
            yield* put(screenRecordingAction(message));
            break;
        }

        case IncomingEvents.SET_GRID_VIEW: {
            if (!Object.values(GRID_VIEW).includes(message.payload.gridView)) {
                return;
            }

            yield* put(setUserPreferredGridViewAction(message.payload));

            break;
        }
        case IncomingEvents.SET_CHAT_VISIBILITY: {
            const isChatAllowed = yield* select(isChatAllowedInConference);
            if (!isChatAllowed) {
                return;
            }

            const chatId = yield* select(getChatId);
            if (message.payload.value && !chatId) {
                yield* put(createChatAction());
            } else {
                yield* put(toggleChatVisibilityAction(message.payload.value));
            }

            break;
        }

        case IncomingEvents.SET_REACTION: {
            yield* put(
                setReactionAction({
                    reactionType: message.payload.value ? message.payload.type : null,
                    reason: 'post-message',
                })
            );
            break;
        }

        case IncomingEvents.SET_PARTICIPANT_LIST_VISIBILITY: {
            yield* put(setSidebarContent(message.payload.value ? SidebarContent.Participants : null));
            break;
        }

        case IncomingEvents.SCROLL_PARTICIPANTS: {
            const sidebarContent = yield* select(getSidebarContent);
            if (sidebarContent !== SidebarContent.Participants) {
                return;
            }

            participantsScrollController.executeScroll(message.payload);
            break;
        }

        case IncomingEvents.SET_GRID_PAGE: {
            if (message.payload.direction === 'next') {
                yield* put(nextGridPageAction());
            } else if (message.payload.direction === 'prev') {
                yield* put(prevGridPageAction());
            }
            break;
        }

        default:
            logger.warn('Unhandled iframe message event', {
                method: 'handleMeetingRoomMessage',
                additional: {
                    message: message.event,
                },
            });
            break;
    }
}
